import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2 } from "lucide-react";
import { api, shortDate } from "../../lib/api";
import { apiError, useAuth } from "../../lib/auth";
import { NeonButton, Panel } from "../common";
import { CodeBlock } from "./CodeBlock";
import { SIGNATURE_VERIFY } from "./catalog";

const SCOPES = ["events:write", "transactions:write", "disputes:read", "verify:session"];
const EVENTS = ["*", "dispute.created", "dispute.won", "dispute.lost", "dispute.accepted", "representment.staged", "representment.submitted", "intercept.received"];
const inp = "bg-field border border-fieldline px-3 py-2 text-sm focus:outline-none focus:border-brand";
const Chip = ({ on, label, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className={`font-mono text-[10px] px-2.5 py-1.5 border transition-colors ${on ? "border-brand text-brand bg-brand/10" : "border-white/15 text-white/50"}`}>{label}</button>
);
const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const toggleEv = (arr, v) => (v === "*" ? ["*"] : toggle(arr.filter((x) => x !== "*"), v));
const OwnerOnly = () => <p className="text-xs text-[#FFB800]" data-testid="hub-owner-only">Only the workspace owner can manage keys and webhooks.</p>;

export const KeysPanel = ({ keys, onChange }) => {
  const { isOwner } = useAuth();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["events:write"]);
  const [fresh, setFresh] = useState(null);
  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/hub/keys", { name, platform: "custom", scopes }); setFresh(data.key); setName(""); onChange(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const revoke = (id) => api.delete(`/hub/keys/${id}`).then(() => { toast.success("Key revoked"); onChange(); }).catch((e) => toast.error(apiError(e)));
  return (
    <Panel title="API keys · per integration" testid="keys-panel">
      {isOwner ? (
        <form onSubmit={create} className="flex flex-wrap items-center gap-3 mb-5" data-testid="key-form">
          <input required placeholder="Key name (e.g. Client ERP)" value={name} onChange={(e) => setName(e.target.value)} className={`${inp} flex-1 min-w-[200px]`} data-testid="key-name-input" />
          {SCOPES.map((s) => <Chip key={s} on={scopes.includes(s)} label={s} onClick={() => setScopes(toggle(scopes, s))} testid={`key-scope-${s.replace(":", "-")}`} />)}
          <NeonButton type="submit" data-testid="key-create-btn">Create key</NeonButton>
        </form>
      ) : <OwnerOnly />}
      {fresh && <div className="mb-5"><CodeBlock title="New key: copy now, it won't be shown again" code={fresh} testid="fresh-key" /></div>}
      <ul className="divide-y divide-divider" data-testid="keys-list">
        {keys.map((k) => (
          <li key={k.id} className="py-3 flex flex-wrap items-center gap-4" data-testid={`key-${k.id}`}>
            <span className="font-mono text-xs w-36">{k.prefix}…</span>
            <span className="text-sm flex-1 min-w-[140px]">{k.name}<span className="block text-[10px] text-white/40">{k.platform} · {k.scopes.join(", ")}</span></span>
            <span className="font-mono text-[10px] text-white/40">{k.uses} calls · last {k.last_used_at ? shortDate(k.last_used_at) : "never"}</span>
            <span className={`font-mono text-[10px] ${k.status === "active" ? "text-brand" : "text-[#F87171]"}`}>{k.status.toUpperCase()}</span>
            {isOwner && k.status === "active" && <button onClick={() => revoke(k.id)} className="font-mono text-[10px] text-[#F87171] hover:underline" data-testid={`key-revoke-${k.id}`}>REVOKE</button>}
          </li>
        ))}
        {!keys.length && <li className="py-3 text-sm text-white/40">No keys yet.</li>}
      </ul>
    </Panel>
  );
};

export const WebhooksPanel = ({ onChange }) => {
  const { isOwner } = useAuth();
  const [eps, setEps] = useState([]);
  const [dl, setDl] = useState([]);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState(["*"]);
  const [secret, setSecret] = useState(null);
  const load = useCallback(() => {
    api.get("/hub/deliveries").then((r) => setDl(r.data)).catch(() => {});
    if (isOwner) api.get("/hub/endpoints").then((r) => setEps(r.data)).catch(() => {});
    onChange?.();
  }, [isOwner, onChange]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => api.get("/hub/deliveries").then((r) => setDl(r.data)).catch(() => {}), 8000); return () => clearInterval(t); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/hub/endpoints", { url, events }); setSecret(data.secret); setUrl(""); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const test = (id) => api.post(`/hub/endpoints/${id}/test`).then((r) => { r.data.ok ? toast.success(`Delivered (HTTP ${r.data.status_code})`) : toast.error(`Failed: ${r.data.status_code || r.data.error}`); load(); });
  return (
    <div className="space-y-6">
      <Panel title="Outbound signed webhooks · for ERP / CRM" testid="webhooks-panel">
        {isOwner ? (
          <form onSubmit={add} className="space-y-3 mb-5" data-testid="endpoint-form">
            <input required type="url" placeholder="https://your-erp.example.com/aegis-webhook" value={url} onChange={(e) => setUrl(e.target.value)} className={`${inp} w-full`} data-testid="endpoint-url-input" />
            <div className="flex flex-wrap gap-2">{EVENTS.map((ev) => <Chip key={ev} on={events.includes(ev)} label={ev === "*" ? "all events" : ev} onClick={() => setEvents(toggleEv(events, ev))} testid={`endpoint-event-${ev === "*" ? "all" : ev}`} />)}</div>
            <NeonButton type="submit" data-testid="endpoint-add-btn">Add endpoint</NeonButton>
          </form>
        ) : <OwnerOnly />}
        {secret && <div className="mb-5"><CodeBlock title="Signing secret: copy now" code={secret} testid="endpoint-secret" /></div>}
        <ul className="divide-y divide-divider" data-testid="endpoints-list">
          {eps.map((e) => (
            <li key={e.id} className="py-3 flex flex-wrap items-center gap-4" data-testid={`endpoint-${e.id}`}>
              <span className="text-sm flex-1 min-w-[220px] break-all">{e.url}<span className="block font-mono text-[10px] text-white/40">{e.events.join(", ")} · {e.secret}</span></span>
              <span className="font-mono text-[10px] text-white/50">last: {e.last_status ?? "—"}</span>
              {isOwner && <>
                <button onClick={() => test(e.id)} className="flex items-center gap-1 font-mono text-[10px] text-brand" data-testid={`endpoint-test-${e.id}`}><Send className="w-3 h-3" /> SEND TEST</button>
                <button onClick={() => api.delete(`/hub/endpoints/${e.id}`).then(load)} className="text-white/40 hover:text-[#F87171]" data-testid={`endpoint-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
              </>}
            </li>
          ))}
          {!eps.length && <li className="py-3 text-sm text-white/40">No endpoints yet.</li>}
        </ul>
      </Panel>
      <Panel title="Recent deliveries · retried up to 3x" testid="deliveries-panel">
        <ul className="space-y-1.5 font-mono text-[11px]">
          {dl.slice(0, 12).map((d) => <li key={d.id} className="flex gap-3"><span className={d.ok ? "text-brand" : "text-[#F87171]"}>{d.ok ? "✓" : "✗"} {d.status_code || "ERR"}</span><span className="text-white/70">{d.event}</span><span className="text-white/35 truncate">{d.url}</span><span className="text-white/35 ml-auto">x{d.attempts}</span></li>)}
          {!dl.length && <li className="text-white/40">No deliveries yet.</li>}
        </ul>
      </Panel>
      <CodeBlock title="Verify signatures (Node)" code={SIGNATURE_VERIFY} testid="signature-verify" />
    </div>
  );
};
